import type { ReactNode } from "react";
import { Label } from "./label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Switch } from "./switch";

export interface PreferenceOption<T extends string> {
  value: T;
  label: string;
}

/**
 * One preference: its name on the left, its control on the right. `htmlFor` is what names that
 * control — both a Radix select trigger and a Radix switch are a `button`, so an associated label is
 * its accessible name.
 */
export const PreferenceRow = ({ children, id, label }: { children: ReactNode; id: string; label: string }) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <Label htmlFor={id} className="font-semibold text-sm">
      {label}
    </Label>
    {children}
  </div>
);

/** A preference chosen from a fixed set, shown as a borderless select. */
export const PreferenceSelectRow = <T extends string>({
  id,
  label,
  onChange,
  options,
  value,
}: {
  id: string;
  label: string;
  onChange: (value: T) => void;
  options: readonly PreferenceOption<T>[];
  value: string;
}) => (
  <PreferenceRow id={id} label={label}>
    <Select
      value={value}
      onValueChange={(nextValue) => {
        // Radix hands back a bare string; only echo a value this row actually offers.
        const selected = options.find((option) => option.value === nextValue);

        if (selected) {
          onChange(selected.value);
        }
      }}
    >
      <SelectTrigger id={id} className="h-auto w-auto gap-1 border-0 bg-transparent px-1 py-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </PreferenceRow>
);

/** A preference that is simply on or off. */
export const PreferenceToggleRow = ({
  checked,
  id,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  id: string;
  label: string;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <PreferenceRow id={id} label={label}>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} className="text-xl" />
  </PreferenceRow>
);

/**
 * One named set of related preferences. `fieldset`/`legend` rather than a heading in a `div` because
 * `legend` *is* the group's accessible name, with no `aria-labelledby` id to keep in sync.
 *
 * `min-w-0` overrides the UA's `min-inline-size: min-content`, which Tailwind's preflight leaves in
 * place and which would otherwise stop a group shrinking with the dialog.
 *
 * The one rule in this dialog separates groups, not rows — it is the container's `divide-y`, and
 * `pb-3` is this group's half of the gap around it, which leaves the rule 25px under the last label
 * and 24px over the next heading. Keep it `pb` rather than `py`: on a `fieldset`, padding-block-start
 * separates the legend from the content instead of moving the legend, so a `pt` here would only push
 * a heading away from the rows it names.
 */
export const PreferenceGroup = ({ children, heading }: { children: ReactNode; heading: string }) => (
  <fieldset className="min-w-0 pb-3 last:pb-0">
    <legend className="mb-1 font-semibold text-muted-foreground text-xs">{heading}</legend>
    {children}
  </fieldset>
);
